// alternative-components/approval/RtpApprovalContainer.tsx
import { useState, useEffect } from "react";
import { type GridRowClickEvent } from "@progress/kendo-react-grid";
import { RemarksModal } from "@/app/models/dialogs/RemarksModal";
import SuccessModalSM from "@/app/models/dialogs/SuccessModalSM";
import { DrawerNoFooter } from "@/app/models/drawer/DrawerNoFooter";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import { DetailsComponent } from "../../../Dashboard/PaymentDetails/DetailsComponent";
import type { ApprovalRequiredTransactions } from "@/app/types/payments";
import RtpPaymentsApproval from "../../../Dashboard/transactions/RtpPaymentsApproval";
import { usePaymentApprovalFlow } from "../../types/usePaymentApprovalFlow";

interface Access {
  view: boolean;
  approve: boolean;
}

interface RtpApprovalContainerProps {
  isOpenRtpList: boolean;
  setIsOpenRtpList: (open: boolean) => void;
  access: Access;
  setCallCount: React.Dispatch<React.SetStateAction<boolean>>;
  preselectedTransId?: number | null;
  initialTransaction?: ApprovalRequiredTransactions | null;
  initialTransactionKey?: number;
}

const RtpApprovalContainer = ({
  isOpenRtpList,
  setIsOpenRtpList,
  access,
  setCallCount,
  initialTransaction,
  initialTransactionKey,
}: RtpApprovalContainerProps) => {
  const [selectedTransactionList, setSelectedTransactionList] = useState<
    number[]
  >([]);
  const [checkAll, setCheckAll] = useState(false);
  const [callPayByBankList, setCallPayByBankList] = useState(false);
  const flow = usePaymentApprovalFlow("RTP");
  const handleRtpInitiatedPaymentDrawer = () => setIsOpenRtpList(false);

  const handleRowClick = (event: GridRowClickEvent) => {
    const row = event?.dataItem as ApprovalRequiredTransactions | undefined;
    if (row) flow.selectTransaction(row);
  };

  const handleBulkApprove = async () => {
    await flow.bulkApproveAndReject(selectedTransactionList, "A");
    setSelectedTransactionList([]);
    setCheckAll(false);
    setCallCount(true);
  };

  const handleBulkReject = () => {
    flow.handleReject();
  };

  const confirmRemarksForBulk = async () => {
    if (selectedTransactionList.length > 0) {
      await flow.bulkApproveAndReject(selectedTransactionList, "R");
      setSelectedTransactionList([]);
      setCheckAll(false);
      setCallCount(true);
      flow.closeRemark();
    } else {
      flow.submitReject();
    }
  };

  const handleSuccessModalClose = () => {
    flow.closeSuccessModal();
    setCallCount(true);
    setCallPayByBankList(true);
  };

  useEffect(() => {
    if (initialTransaction) {
      flow.selectTransaction(initialTransaction);
    }
  }, [initialTransactionKey]);

  return (
    <>
      <RtpPaymentsApproval
        openRtpInitiatedPayment={isOpenRtpList}
        handleRtpInitiatedPaymentDrawer={handleRtpInitiatedPaymentDrawer}
        handleRowClick={handleRowClick}
        checkAll={checkAll}
        selectedTransactionList={selectedTransactionList}
        setSelectedTransactionList={setSelectedTransactionList}
        setCheckAll={setCheckAll}
        handleBulkReject={handleBulkReject}
        handleBulkApprove={handleBulkApprove}
        callPayByBankList={callPayByBankList}
        setCallPayByBankList={setCallPayByBankList}
      />

      <DrawerNoFooter
        title="RTP Payment"
        subtitle="Details"
        openDrawer={flow.openApprovalDrawer}
        onClose={() => flow.setOpenApprovalDrawer(false)}
      >
        <DetailsComponent
          transactionDetailsLoader={flow.transactionDetailsLoader}
          transaction={flow.transactionDetails}
          handleReject={flow.handleReject}
          handleSentForRepair={flow.handleReject}
          handleApprove={flow.handleApprove}
          hideFooter={!access?.approve}
          schedule={false}
          showEdit={false}
          fromRtp={true}
        />
      </DrawerNoFooter>

      {flow.openRejectAndNoteModal && (
        <RemarksModal
          isOpen={flow.openRejectAndNoteModal}
          onClose={flow.closeRemark}
          errors={{}}
          setValue={() => {}}
          isReject={flow.isReject}
          value={flow.remark}
          onChange={flow.setRemark}
          submit={confirmRemarksForBulk}
        />
      )}

      {flow.openSuccessModal && (
        <SuccessModalSM
          onClose={handleSuccessModalClose}
          isOpen={flow.openSuccessModal}
          successData={flow.successData}
          bulkApproveCount={flow.bulkApproveCount}
        />
      )}

      {flow.isLoading && <SubmitLoading />}
    </>
  );
};

export default RtpApprovalContainer;
